import React, { createContext, useContext, useState, useEffect } from "react";
import { handleBackgroundScroll } from "../utils/backgroundScroll";
import { log } from "../utils/logger";
/**
* Shape of the Search Window Context value.
*/
interface SearchWindowContextType {
    /** Whether the Search window is currently open */
    isOpen: boolean;
    /** Function to open the Search window*/
    open: () => void;
    /** Function to close the Search window and reset target */
    close: () => void;
    /** Last search - used when closing with detail card */
    last: string;
    /** Last search setter - used when closing with detail card */
    setLast: (val: string )=> void;
}

/**
* React context to manage state and actions for a Search.
* Provides  open state, open/close methods.
*/
const SearchWindowContext = createContext<SearchWindowContextType | undefined>(undefined);

/**
* Provider component that manages Search state and provides context to children.
* It also locks background scroll while the  is open.
* 
* @param children - React child elements
*/
export const SearchProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [isOpen, setIsOpen] = useState(false); // Open flag
    const [last, setLast] = useState<string>("");
    const open = () => setIsOpen(true); // Open Search window
    const close = () => { // Close Search window
        setIsOpen(false);
    };
    // Stop scroll on open
    useEffect(()=>{
        if(last !== "" && !isOpen) return 
        handleBackgroundScroll(isOpen)
    }, [isOpen])

    return (
        <SearchWindowContext.Provider value={{ isOpen, open, close, last, setLast}}>
        {children}
        </SearchWindowContext.Provider>
    );
};
/**
* Hook to access the SearchWindowContext.
* Throws an error if used outside of a SearchProvider.
* 
* @returns SearchWindowContextType object with  state and control functions
*/
export const useSearchWindow = () => {
    const context = useContext(SearchWindowContext);
    if (!context) throw new Error("useSearchWindow must be used within a SearchProvider");
    return context;
};
